import React from 'react'

import {
  Box,
  Button,
  Color,
  Display,
  FlexDirection,
  Image,
  Overflow,
  StepperButton,
  Text,
  withCitrusTheme,
} from '@gousto-internal/citrus-react'

import { getProductImage } from './image'
import type { BasketUpsellProduct } from './types'

const ProductImage = withCitrusTheme(Image, () => ({
  objectFit: 'cover',
  objectPosition: 'center',
  imageRendering: 'auto',
}))

type ProductTileProps = {
  product: BasketUpsellProduct
  onClickProduct?: (product: BasketUpsellProduct) => void
}

export const BasketUpsellProductTile = ({ product, onClickProduct }: ProductTileProps) => {
  const image = getProductImage(product)
  const isUnavailable = !product.isAvailable || !!product.limitReached
  const handleTileClick = () => onClickProduct?.(product)
  const stopClickPropagation = (event: React.MouseEvent) => event.stopPropagation()

  return (
    <Box
      bg={Color.White}
      data-testid="basket-upsell-product-tile"
      display={Display.Flex}
      flexDirection={FlexDirection.Column}
      height="100%"
      overflowX={Overflow.Hidden}
      overflowY={Overflow.Hidden}
      onClick={handleTileClick}
      onKeyDown={(event) => {
        if (!onClickProduct) return
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault()
          handleTileClick()
        }
      }}
      role={onClickProduct ? 'button' : undefined}
      style={{
        borderRadius: '4px',
        cursor: onClickProduct ? 'pointer' : 'default',
      }}
      tabIndex={onClickProduct ? 0 : undefined}
    >
      <Box height="136px" width="100%">
        {image.src && (
          <ProductImage
            alt={image.alt}
            sizes="220px"
            src={image.src}
            srcSet={image.srcSet}
            width="100%"
            height="100%"
          />
        )}
      </Box>
      <Box
        display={Display.Flex}
        flexDirection={FlexDirection.Column}
        flexGrow={1}
        gap={2}
        paddingH={3}
        paddingV={3}
      >
        <Box flexGrow={1}>
          <Text color={Color.ColdGrey_800} fontWeight={700} size={3}>
            {product.title}
          </Text>
        </Box>
        <Box display={Display.Flex} flexDirection={FlexDirection.Column} gap={2}>
          <Text color={Color.ColdGrey_700} size={1}>
            {`${product.listPrice} • 1 serving`}
          </Text>
          {product.qty > 0 ? (
            <Box onClick={stopClickPropagation}>
              <StepperButton
                count={product.qty}
                decrement={product.onRemove}
                disableIncrement={isUnavailable}
                fullWidth
                increment={product.onAdd}
                incrementLabel="Add item"
              >
                {product.qty}
              </StepperButton>
            </Box>
          ) : (
            <Button
              aria-label="Add product"
              disabled={isUnavailable}
              onClick={(event) => {
                event.stopPropagation()
                product.onAdd()
              }}
              width="100%"
            >
              Add
            </Button>
          )}
        </Box>
      </Box>
    </Box>
  )
}
